import { createApi } from "@reduxjs/toolkit/query/react";
import { customFetchBase } from "../../utils/authentication/fetchBaseQuery";
import {
  transformErrorResponse,
  transformSuccessResponse,
} from "../../utils/middleware/responseMiddleware2";

export const NOTIFICATION_API_ENDPOINTS = Object.freeze({
  unreadCount: "/Notification/GetUnreadNotificationCount",
  recent: "/Notification/GetRecentNotifications",
  history: "/Notification/GetNotifications",
  markRead: "/Notification/MarkNotificationAsRead",
  markAllRead: "/Notification/MarkAllNotificationsAsRead",
});

export const notificationApi = createApi({
  reducerPath: "notificationApi",
  baseQuery: customFetchBase,
  tagTypes: ["NotificationUnreadCount", "NotificationRecent", "NotificationHistory"],
  keepUnusedDataFor: 60,
  refetchOnReconnect: true,
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    getUnreadNotificationCount: builder.query({
      query: () => ({
        url: NOTIFICATION_API_ENDPOINTS.unreadCount,
        method: "GET",
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      providesTags: [{ type: "NotificationUnreadCount", id: "CURRENT" }],
    }),
    getRecentNotifications: builder.query({
      query: ({ pageNumber = 1, pageSize = 10 } = {}) => ({
        url: NOTIFICATION_API_ENDPOINTS.recent,
        method: "GET",
        params: {
          pageNumber,
          pageSize,
        },
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      providesTags: (result) => [
        { type: "NotificationRecent", id: "LIST" },
        ...(result?.data?.data ?? result?.data ?? [])
          .map((item) => item?.notificationId)
          .filter(Boolean)
          .map((notificationId) => ({
            type: "NotificationRecent",
            id: notificationId,
          })),
      ],
    }),
    getNotifications: builder.query({
      query: ({
        pageNumber = 1,
        pageSize = 25,
        isRead,
        type,
        priority,
      } = {}) => ({
        url: NOTIFICATION_API_ENDPOINTS.history,
        method: "GET",
        params: {
          pageNumber,
          pageSize,
          ...(typeof isRead === "boolean" ? { isRead } : {}),
          ...(type ? { type } : {}),
          ...(priority ? { priority } : {}),
        },
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      providesTags: (result) => [
        { type: "NotificationHistory", id: "LIST" },
        ...(result?.data?.data ?? result?.data ?? [])
          .map((item) => item?.notificationId)
          .filter(Boolean)
          .map((notificationId) => ({
            type: "NotificationHistory",
            id: notificationId,
          })),
      ],
    }),
    markNotificationAsRead: builder.mutation({
      query: ({ notificationId }) => ({
        url: NOTIFICATION_API_ENDPOINTS.markRead,
        method: "POST",
        body: {
          notificationId,
        },
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      invalidatesTags: [
        { type: "NotificationUnreadCount", id: "CURRENT" },
        { type: "NotificationRecent", id: "LIST" },
        { type: "NotificationHistory", id: "LIST" },
      ],
    }),
    markAllNotificationsAsRead: builder.mutation({
      query: () => ({
        url: NOTIFICATION_API_ENDPOINTS.markAllRead,
        method: "POST",
        body: {},
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      invalidatesTags: [
        { type: "NotificationUnreadCount", id: "CURRENT" },
        { type: "NotificationRecent", id: "LIST" },
        { type: "NotificationHistory", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetUnreadNotificationCountQuery,
  useGetRecentNotificationsQuery,
  useLazyGetRecentNotificationsQuery,
  useGetNotificationsQuery,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
} = notificationApi;
